import { useMutation, useQuery } from '@tanstack/react-query';
import {
  NearbySpotsResponseSchema,
  SpotResponseSchema,
  CheckInResponseSchema,
  type CreateSpot,
  type CheckIn,
} from '@web/shared';
import { apiRequest } from '@/lib/api';
import type { Coords } from '@/lib/geo';

export function useNearbySpots(coords: Coords | null) {
  return useQuery({
    queryKey: ['spots', 'nearby', coords?.lat?.toFixed(3), coords?.lng?.toFixed(3)],
    enabled: !!coords,
    queryFn: ({ signal }) =>
      apiRequest(`/api/spots/nearby?lat=${coords!.lat}&lng=${coords!.lng}`, {
        schema: NearbySpotsResponseSchema,
        signal,
      }),
  });
}

export function useSpot(spotId: string) {
  return useQuery({
    queryKey: ['spot', spotId],
    queryFn: ({ signal }) =>
      apiRequest(`/api/spots/${spotId}`, { schema: SpotResponseSchema, signal }),
  });
}

export function useSpotByCode() {
  return useMutation({
    mutationFn: (code: string) =>
      apiRequest(`/api/spots/by-code/${encodeURIComponent(code.toUpperCase())}`, {
        schema: SpotResponseSchema,
      }),
  });
}

export function useCreateSpot() {
  return useMutation({
    mutationFn: (body: CreateSpot) =>
      apiRequest('/api/spots', { method: 'POST', body, schema: SpotResponseSchema }),
  });
}

export function useOpenCircle() {
  return useMutation({
    mutationFn: (coords: Coords) =>
      apiRequest('/api/spots/open-circle', {
        method: 'POST',
        body: { lat: coords.lat, lng: coords.lng },
        schema: SpotResponseSchema,
      }),
  });
}

export function useCheckIn(spotId: string) {
  return useMutation({
    mutationFn: (body: CheckIn) =>
      apiRequest(`/api/spots/${spotId}/check-in`, {
        method: 'POST',
        body,
        schema: CheckInResponseSchema,
      }),
  });
}
